import axios from 'axios';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.caply4pe.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Interceptor to inject bearer auth token from localStorage if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('caply_auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Dev-auth tenant selection (multi-tenant isolation testing)
  const tenant = localStorage.getItem('caply_tenant');
  if (tenant) {
    config.headers['X-Dev-Tenant'] = tenant;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor for response handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn('Authentication token expired or invalid.');
    }
    // 402 = the workspace hit a plan limit. Let the shell surface an upgrade
    // prompt instead of each page inventing its own error copy.
    if (error.response && error.response.status === 402) {
      const detail = error.response.data && error.response.data.detail;
      window.dispatchEvent(new CustomEvent('caply:limit', {
        detail: typeof detail === 'object' && detail ? detail : { message: String(detail || '') },
      }));
    }
    // 403 = the read-only demo refused a change. Surface one shared notice
    // instead of each page inventing its own copy.
    if (error.response && error.response.status === 403
        && error.response.data && error.response.data.code === 'demo_read_only') {
      window.dispatchEvent(new CustomEvent('caply:demo', {
        detail: { message: error.response.data.detail },
      }));
    }
    return Promise.reject(error);
  }
);

/**
 * A server error, as one readable sentence.
 *
 * FastAPI answers validation problems with a LIST of {loc, msg, type} objects.
 * Passing that straight into a banner makes React throw (#31: objects are not
 * valid as a React child) and the whole page dies, so the message is always
 * flattened to text here.
 */
export function errorText(err, fallback = 'Something went wrong. Please try again.') {
  const detail = err?.response?.data?.detail;
  const flatten = (value) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value
        .map((item) => flatten(item))
        .filter(Boolean)
        .join(' ');
    }
    if (typeof value === 'object') {
      const field = Array.isArray(value.loc)
        ? value.loc.filter((part) => part !== 'body').join('.')
        : value.field || '';
      const message = value.msg || value.message || value.error || '';
      if (message) return field ? `${field}: ${message}` : message;
      return Object.entries(value)
        .filter(([, v]) => typeof v !== 'object')
        .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
        .join(', ');
    }
    return String(value);
  };

  return flatten(detail) || err?.message || fallback;
}

export default api;
